/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
 */

import type { VNode } from "preact";
import { useMemo } from "preact/hooks";
import { MerchantAuthMethod, TalerMerchantApi } from "@gnu-taler/taler-util";
import { useLocation } from "wouter-preact";
import {
  useManagedInstance,
  useManagedInstanceKyc,
  useManagedInstances,
  useMerchantConfig,
} from "../api/hooks.js";
import { runProtectedMutation } from "../api/contracts.js";
import type { PendingProtectedAction } from "../api/protectedAction.js";
import { AdminAccountsScreen } from "../screens/AdminAccountsScreen.js";
import {
  AdminAccountFormScreen,
  type AdminAccountFormValue,
} from "../screens/AdminAccountFormScreen.js";
import { AdminAccountDetailScreen } from "../screens/AdminAccountDetailScreen.js";
import { AdminAccountCredentialsScreen } from "../screens/AdminAccountCredentialsScreen.js";
import { useTranslation } from "../context/translation.js";

function reconfiguration(
  value: AdminAccountFormValue,
): TalerMerchantApi.InstanceReconfigurationMessage {
  return {
    name: value.name,
    email: value.email,
    phone_number: value.phone_number,
    website: value.website,
    logo: value.logo,
    address: value.address,
    jurisdiction: value.jurisdiction,
    use_stefan: value.use_stefan,
    default_pay_delay: value.default_pay_delay,
    default_refund_delay: value.default_refund_delay,
    default_wire_transfer_delay: value.default_wire_transfer_delay,
    default_wire_transfer_rounding_interval:
      value.default_wire_transfer_rounding_interval,
  };
}

function formValue(
  instanceId: string,
  details: TalerMerchantApi.QueryInstancesResponse,
): AdminAccountFormValue {
  return {
    id: instanceId,
    name: details.name,
    email: details.email,
    phone_number: details.phone_number,
    website: details.website,
    logo: details.logo,
    address: details.address,
    jurisdiction: details.jurisdiction,
    use_stefan: details.use_stefan,
    default_pay_delay: details.default_pay_delay,
    default_refund_delay: details.default_refund_delay,
    default_wire_transfer_delay: details.default_wire_transfer_delay,
    default_wire_transfer_rounding_interval:
      details.default_wire_transfer_rounding_interval,
  };
}

export function AdminAccountsRoute({
  onMfaRequired,
  onSignIn,
}: {
  onMfaRequired: (action: PendingProtectedAction) => void;
  onSignIn: (instanceId: string) => void;
}): VNode {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const managed = useManagedInstances();

  const destructive = async (
    instance: TalerMerchantApi.Instance,
    purge: boolean,
  ) => {
    const result = await managed.deleteInstance(instance.id, purge);
    if (result.challengeResponse) {
      onMfaRequired({
        accountLabel: instance.name,
        challengeAccount: instance.id,
        actionNotice: purge
          ? t`Permanently purging merchant account ${instance.id}`
          : t`Disabling merchant account ${instance.id}`,
        challengeResponse: result.challengeResponse,
        cancelTo: "/admin/accounts",
        continueWith: (challengeIds) =>
          runProtectedMutation(async () => {
            const continued = await managed.deleteInstance(
              instance.id,
              purge,
              challengeIds,
            );
            if (continued.challengeResponse)
              return {
                type: "challenge" as const,
                challenge: continued.challengeResponse,
              };
            return { redirectTo: "/admin/accounts" };
          }),
        revalidate: managed.resource.refresh,
      });
    }
  };

  return (
    <AdminAccountsScreen
      instances={managed.instances}
      resource={managed.resource}
      accessDenied={managed.accessDenied}
      onCreate={() => setLocation("/admin/accounts/new")}
      onDisable={(instance) => destructive(instance, false)}
      onPurge={(instance) => destructive(instance, true)}
      onSignIn={(instance) => onSignIn(instance.id)}
    />
  );
}

export function AdminAccountCreateRoute({
  onMfaRequired,
}: {
  onMfaRequired: (action: PendingProtectedAction) => void;
}): VNode {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const managed = useManagedInstances();
  const { phoneRegex } = useMerchantConfig();
  const submit = async (value: AdminAccountFormValue) => {
    const payload: TalerMerchantApi.InstanceConfigurationMessage = {
      ...reconfiguration(value),
      id: value.id,
      auth: { method: MerchantAuthMethod.TOKEN, password: value.password! },
    };
    const result = await managed.createInstance(payload);
    if (result.challengeResponse) {
      onMfaRequired({
        accountLabel: value.name,
        challengeAccount: value.id,
        actionNotice: t`Creating merchant account ${value.id}`,
        challengeResponse: result.challengeResponse,
        cancelTo: "/admin/accounts/new",
        continueWith: (challengeIds) =>
          runProtectedMutation(async () => {
            const continued = await managed.createInstance(
              payload,
              challengeIds,
            );
            if (continued.challengeResponse)
              return {
                type: "challenge" as const,
                challenge: continued.challengeResponse,
              };
            return { redirectTo: "/admin/accounts" };
          }),
        revalidate: managed.resource.refresh,
      });
      return;
    }
    setLocation("/admin/accounts");
  };
  return (
    <AdminAccountFormScreen
      mode="create"
      phoneRegex={phoneRegex}
      onSubmit={submit}
      onBack={() => setLocation("/admin/accounts")}
    />
  );
}

export function AdminAccountDetailRoute({
  instanceId,
  tab = "overview",
  onSignIn,
}: {
  instanceId: string;
  tab?: "overview" | "verification";
  onSignIn: (instanceId: string) => void;
}): VNode {
  const [, setLocation] = useLocation();
  const managed = useManagedInstance(instanceId);
  const kyc = useManagedInstanceKyc(instanceId, tab === "verification");
  return (
    <AdminAccountDetailScreen
      instanceId={instanceId}
      details={managed.details}
      resource={managed.resource}
      tab={tab}
      kyc={kyc.kyc}
      kycResource={tab === "verification" ? kyc.resource : undefined}
      onBack={() => setLocation("/admin/accounts")}
      onEdit={() =>
        setLocation(`/admin/accounts/${encodeURIComponent(instanceId)}/edit`)
      }
      onCredentials={() =>
        setLocation(
          `/admin/accounts/${encodeURIComponent(instanceId)}/credentials`,
        )
      }
      onSignIn={() => onSignIn(instanceId)}
    />
  );
}

export function AdminAccountEditRoute({
  instanceId,
  onMfaRequired,
}: {
  instanceId: string;
  onMfaRequired: (action: PendingProtectedAction) => void;
}): VNode {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const managed = useManagedInstance(instanceId);
  const { phoneRegex } = useMerchantConfig();
  const initial = useMemo(
    () =>
      managed.details ? formValue(instanceId, managed.details) : undefined,
    [instanceId, managed.details],
  );
  if (!managed.details) {
    return (
      <AdminAccountDetailScreen
        instanceId={instanceId}
        resource={managed.resource}
        onBack={() => setLocation("/admin/accounts")}
        onEdit={() => undefined}
        onCredentials={() => undefined}
        onSignIn={() => undefined}
      />
    );
  }
  const submit = async (value: AdminAccountFormValue) => {
    const payload = reconfiguration(value);
    const result = await managed.updateInstance(payload);
    if (result.challengeResponse) {
      onMfaRequired({
        accountLabel: value.name,
        challengeAccount: instanceId,
        actionNotice: t`Updating merchant account ${instanceId}`,
        challengeResponse: result.challengeResponse,
        cancelTo: `/admin/accounts/${encodeURIComponent(instanceId)}/edit`,
        continueWith: (challengeIds) =>
          runProtectedMutation(async () => {
            const continued = await managed.updateInstance(
              payload,
              challengeIds,
            );
            if (continued.challengeResponse)
              return {
                type: "challenge" as const,
                challenge: continued.challengeResponse,
              };
            return {
              redirectTo: `/admin/accounts/${encodeURIComponent(instanceId)}`,
            };
          }),
        revalidate: managed.resource.refresh,
      });
      return;
    }
    setLocation(`/admin/accounts/${encodeURIComponent(instanceId)}`);
  };
  return (
    <AdminAccountFormScreen
      mode="edit"
      initial={initial!}
      phoneRegex={phoneRegex}
      onSubmit={submit}
      onBack={() =>
        setLocation(`/admin/accounts/${encodeURIComponent(instanceId)}`)
      }
    />
  );
}

export function AdminAccountCredentialsRoute({
  instanceId,
  onMfaRequired,
}: {
  instanceId: string;
  onMfaRequired: (action: PendingProtectedAction) => void;
}): VNode {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const managed = useManagedInstance(instanceId);
  const reset = async (password: string) => {
    const result = await managed.resetPassword(password);
    if (result.challengeResponse) {
      onMfaRequired({
        accountLabel: managed.details?.name || instanceId,
        challengeAccount: instanceId,
        actionNotice: t`Resetting the password for merchant account ${instanceId}`,
        challengeResponse: result.challengeResponse,
        cancelTo: `/admin/accounts/${encodeURIComponent(instanceId)}/credentials`,
        continueWith: (challengeIds) =>
          runProtectedMutation(async () => {
            const continued = await managed.resetPassword(
              password,
              challengeIds,
            );
            if (continued.challengeResponse)
              return {
                type: "challenge" as const,
                challenge: continued.challengeResponse,
              };
            return {
              redirectTo: `/admin/accounts/${encodeURIComponent(instanceId)}`,
            };
          }),
      });
      return;
    }
    setLocation(`/admin/accounts/${encodeURIComponent(instanceId)}`);
  };
  return (
    <AdminAccountCredentialsScreen
      instanceId={instanceId}
      onBack={() =>
        setLocation(`/admin/accounts/${encodeURIComponent(instanceId)}`)
      }
      onReset={reset}
    />
  );
}
